/*
 * Created by Volodymyr Mak. on 30/6/2020.
 * Copyright © 2020 Curriculum Ltd. All rights reserved.
 */

import { Component, OnInit, OnDestroy, Inject } from '@angular/core';

import {BehaviorSubject, of, Subject, Subscription, tap} from 'rxjs';

import { ActivatedRoute } from '@angular/router';
import { getRestApiProvider, Paginated, RestApiService } from '../../shared/api/rest-api.service';
import { Course, CourseDetails } from '../../shared-pages/courses/course';
import {switchMap, takeUntil} from 'rxjs/operators';
import { View } from '../../shared/components/course-card/course-card.component';
import { CoursesService } from '../../shared-pages/courses/courses.service';
import { Series } from '../../shared/graphs/school-performance-histogram/series.model';
import { TeacherProfileService } from './teacher-profile.service';
import { IPermissions } from './teacher-profile.interface';
import { ToastrService } from 'ngx-toastr';
import { getTimeZones } from '@vvo/tzdb';
import { Role } from '../../shared/role';

@Component({
  selector: 'curr-teacher-profile',
  templateUrl: './teacher-profile.component.html',
  styleUrls: ['./teacher-profile.component.scss'],
  providers: [getRestApiProvider<Course>('schoolYearCourses/getTeacherSubjects', 'getTeacherSubjects')]
})
export class TeacherProfileComponent implements OnInit, OnDestroy {
  filterChangedSubscription: Subscription;
  filterChanged$ = new BehaviorSubject({});
  profileData: any;
  currentProfileData: any;
  options = [];
  value = [];
  cards = [];
  paginated$ = new BehaviorSubject(new Paginated(10, 0, 10));
  chartData: Series[] = [
    {
      data: [
        {
          date: '2012-1-1',
          value: 2
        },
        {
          date: '2012-2-2',
          value: 3
        },
        {
          date: '2012-3-3',
          value: 4
        },
        {
          date: '2012-4-4',
          value: 3
        },
        {
          date: '2012-5-5',
          value: 2
        },
        {
          date: '2012-6-6',
          value: 3
        },
        {
          date: '2012-7-7',
          value: 4
        },
        {
          date: '2012-8-8',
          value: 3
        }
      ],
      name: 'test',
      cssClasses: { line: 'green-statistic', dot: 'green-statistic-dot' }
    }
  ];
  path = [
    { label: 'Home', url: '/dashboard' },
    { label: 'Users', url: '' }
  ];

  initPath = [
    { label: 'Home', url: '/dashboard' },
    { label: 'Users', url: '' }
  ];

  public showTimeZone = false;
  public showEmptySection = false;
  public isDisabledPermissions = false;
  public timezoneOptions = [];
  public selectedTimezone: string;
  public showCourses = false;
  public showTimezoneSection = false;
  private subscription = new Subscription();
  destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(private route: ActivatedRoute,
              private coursesService: CoursesService,
              private teacherProfileService: TeacherProfileService,
              private toastr: ToastrService,
              @Inject('getTeacherSubjects') private teacherCoursesService: RestApiService<CourseDetails>
  ) {
  }

  ngOnInit(): void {
    this.route.data
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        this.profileData = data.profileData;
        this.currentProfileData = data.currentProfileData;

        this.init();

        this.showTimezoneSection = this.currentProfileData && this.profileData.id === this.currentProfileData.id;

        if (!this.profileData.multipleSchoolsAccess) {
          this.showCourses = true;
        } else {
          this.showCourses = false;
        }

        if (this.profileData.user_type === 'SCHOOL_ADMIN') {
          const zones = getTimeZones();

          this.timezoneOptions = zones.map(tz => {
            const offset = this.formatOffset(tz.currentTimeOffsetInMinutes);
            return {
              value: tz.name,
              label: `(GMT${offset}) ${tz.mainCities.join(', ')}`
            };
          });

          if (this.timezoneOptions.length > 0) {
            this.selectedTimezone = this.profileData.timeZone ?? this.timezoneOptions[0].value;
          }

          this.showTimeZone = true;
          // this.teacherProfileService.getTimezone()
          //   .pipe(takeUntil(this.destroy$))
          //   .subscribe((res: any) => {
          //     this.timezoneOptions = res.map(tz => ({
          //       value: tz.displayName,
          //       label: tz.offset
          //     }));
          //     if (this.timezoneOptions.length > 0) {
          //       this.selectedTimezone = this.profileData.timeZone ?? this.timezoneOptions[0].value;
          //     }
          //     this.showTimeZone = true;
          //   });
        }

        if (this.profileData.user_type !== 'SCHOOL_ADMIN' || this.profileData.multipleSchoolsAccess) {
          this.showEmptySection = true;
          return;
        }

        this.isDisabledPermissions = !this.currentProfileData.multipleSchoolsAccess;

        // Map permission keys to human-readable labels
        const permissionLabels = {
          schoolStatistics: 'Able to See All Schools Statistic',
          schoolSubjects: 'Able to See All Schools Subjects',
          manageCurriculums: 'Able to Manage Curriculums',
          beTeacher: 'Able to Be a Teacher',
          manageApiKeys: 'Able to Managing API Keys'
        };

        // Create options array from permissions
        this.options = Object.keys(this.profileData.permissions)
          .filter(key => key !== 'userId') // Exclude userId
          .map(key => ({
            value: key,
            label: permissionLabels[key] || key, // Use mapped label or key if no mapping exists
            disabled: false
          }));

        // Set value array to include keys of permissions that are true
        this.value = Object.keys(this.profileData.permissions)
          .filter(key => key !== 'userId' && this.profileData.permissions[key] === true);

        // this.path = this.initPath.slice();
        // this.path.push({ label: `${this.profileData.first_name} ${this.profileData.last_name}`, url: '' });
      });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.subscription.unsubscribe();
  }

  formatOffset(minutes: number): string {
    const sign = minutes >= 0 ? '+' : '-';
    const absMinutes = Math.abs(minutes);
    const hh = String(Math.floor(absMinutes / 60)).padStart(2, '0');
    const mm = String(absMinutes % 60).padStart(2, '0');
    return `${sign}${hh}:${mm}`;
  }

  init() {
    this.paginated$ = new BehaviorSubject(new Paginated(10, 0, 10));
    this.cards = [];
    this.filterChangedSubscription = this.filterChanged$
      .pipe(
        switchMap(res => {
          res = { ...res, userId: this.profileData.id };
          return this.teacherCoursesService.getAllWithPostPaginated(this.paginated$, res);
        })
      )
      .subscribe(res => {
        this.cards.push(...this.coursesService.createCourseCards(res, this.currentProfileData));
        this.cards.forEach((card, index) => {
          card.title.topRightIcons = [
            { icon: 'sandwich', link: '/#', viewToSwitch: View.StandardView },
            { icon: 'graph', link: '/#', viewToSwitch: View.GraphView }
          ];
          // tslint:disable-next-line
          card['chartData'] = of(this.chartData);
        });
      });
  }

  filterChanged(event) {
    this.filterChangedSubscription.unsubscribe();
    this.filterChanged$.next(event);
    this.init();
  }

  showMore() {
    this.paginated$.next(this.paginated$.value.next());
  }

  onPermissionsChange(selectedPermissions: string[]) {
    // Create a permissions object with all permissions set to false initially
    const updatedPermissions: IPermissions = {
      userId: this.profileData.id,
      schoolStatistics: false,
      schoolSubjects: false,
      manageCurriculums: false,
      manageApiKeys: false,
      beTeacher: false
    };

    // Set the selected permissions to true
    selectedPermissions.forEach(permission => {
      updatedPermissions[permission] = true;
    });

    // Call the service to update permissions
    this.teacherProfileService.updatePermissions(updatedPermissions)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        () => {
          // Update the local permissions object to reflect the changes
          this.profileData.permissions = updatedPermissions;
          this.toastr.success('Permission was updated successfully.');
        },
        error => {
          this.toastr.warning(error);
          // Revert the value array to match the original permissions if there's an error
          this.value = Object.keys(this.profileData.permissions)
            .filter(key => key !== 'userId' && this.profileData.permissions[key] === true);
        }
      );
  }

  onTimezoneChange(selectedTimezone: string) {
    this.teacherProfileService.updateTimezone(selectedTimezone)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        () => {
          this.selectedTimezone = selectedTimezone;
          this.toastr.success('Timezone was updated successfully.');
        },
        error => {
          console.error('Error updating timezone:', error);
          this.toastr.error('Failed to update timezone. Please try again.');
        }
      );
  }
}
